import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { Input } from 'components/FormFields';

// Form card container
const FormCard = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`;

const FormSection = styled.div`
  margin-bottom: ${({ noMargin }) => (noMargin ? '0' : '24px')};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#0F172A'};
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${({ columns }) => columns || '1fr 1fr'};
  gap: 16px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

// Toggle row styles (matching HardwareTab)
const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: ${({ theme }) =>
    theme?.colors?.background?.overlay ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)')};
  border-radius: 8px;
  margin-top: ${({ first }) => (first ? '0' : '12px')};
`;

const ToggleInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ToggleIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.08)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fa4616;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ToggleText = styled.div``;

const ToggleLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#0F172A'};
`;

const ToggleDescription = styled.div`
  font-size: 13px;
  color: ${({ theme }) =>
    theme?.colors?.textGray?.secondary ||
    (theme?.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin-top: 2px;
`;

// Toggle switch
const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 26px;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background: linear-gradient(135deg, #fa4616, #ff6b3b);
  }

  &:checked + span:before {
    transform: translateX(22px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) =>
    theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgb(226, 232, 240)'};
  transition: all 0.3s ease;
  border-radius: 26px;

  &:before {
    position: absolute;
    content: '';
    height: 20px;
    width: 20px;
    left: 3px;
    top: 3px;
    background-color: #fff;
    transition: all 0.3s ease;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

// Icons
const CreditCardIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const DollarIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const GlobeIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const UserCheckIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <polyline points="17 11 19 13 23 9" />
  </svg>
);

const PaymentsTab = ({ values, setFieldValue }) => {
  const handleChange = (field) => (e) => {
    setFieldValue(field, e.target.value);
  };

  const handleToggleChange = (field) => (e) => {
    const isChecked = e.target.checked;

    if (field === 'all_in_pricing') {
      // All In Pricing = show_fee: false (promoter absorbs)
      setFieldValue('show_fee', !isChecked);
      if (isChecked) {
        // Mutually exclusive: turn off Cash Back
        setFieldValue('marketing_fee', false);
      }
    } else if (field === 'customer_pays_fees') {
      // Customer Pays Fees = show_fee: true
      setFieldValue('show_fee', isChecked);
      if (!isChecked) {
        // Turning off Customer Pays = All In Pricing ON, so turn off Cash Back
        setFieldValue('marketing_fee', false);
      }
    } else {
      setFieldValue(field, isChecked);
    }
  };

  return (
    <FormCard>
      <FormSection noMargin>
        <SectionTitle>
          <CreditCardIcon />
          Payment Settings
        </SectionTitle>

        <FormRow>
          <FormGroup>
            <Input
              label="Stripe Account ID"
              value={values.stripe_account_id || ''}
              onChange={handleChange('stripe_account_id')}
              placeholder="acct_xxxxxxxxxx"
            />
          </FormGroup>
          <FormGroup />
        </FormRow>

        <ToggleRow first>
          <ToggleInfo>
            <ToggleIcon>
              <DollarIcon />
            </ToggleIcon>
            <ToggleText>
              <ToggleLabel>All In Pricing</ToggleLabel>
              <ToggleDescription>
                Promoter pays ticket fee and order processing fee
              </ToggleDescription>
            </ToggleText>
          </ToggleInfo>
          <Toggle>
            <ToggleInput
              type="checkbox"
              checked={!values.show_fee}
              onChange={handleToggleChange('all_in_pricing')}
            />
            <ToggleSlider />
          </Toggle>
        </ToggleRow>

        <ToggleRow>
          <ToggleInfo>
            <ToggleIcon>
              <UserCheckIcon />
            </ToggleIcon>
            <ToggleText>
              <ToggleLabel>Customer Pays Fees</ToggleLabel>
              <ToggleDescription>
                Customer pays ticket fee and order processing fee
              </ToggleDescription>
            </ToggleText>
          </ToggleInfo>
          <Toggle>
            <ToggleInput
              type="checkbox"
              checked={!!values.show_fee}
              onChange={handleToggleChange('customer_pays_fees')}
            />
            <ToggleSlider />
          </Toggle>
        </ToggleRow>

        <ToggleRow>
          <ToggleInfo>
            <ToggleIcon>
              <CreditCardIcon />
            </ToggleIcon>
            <ToggleText>
              <ToggleLabel>1% Cash Back</ToggleLabel>
              <ToggleDescription>
                Collect 1% for cash back program
              </ToggleDescription>
            </ToggleText>
          </ToggleInfo>
          <Toggle>
            <ToggleInput
              type="checkbox"
              checked={!!values.marketing_fee}
              onChange={handleToggleChange('marketing_fee')}
            />
            <ToggleSlider />
          </Toggle>
        </ToggleRow>

        <ToggleRow>
          <ToggleInfo>
            <ToggleIcon>
              <GlobeIcon />
            </ToggleIcon>
            <ToggleText>
              <ToggleLabel>Collect International Card Fees</ToggleLabel>
              <ToggleDescription>
                Collect additional fees incurred for out-of-country cards
              </ToggleDescription>
            </ToggleText>
          </ToggleInfo>
          <Toggle>
            <ToggleInput
              type="checkbox"
              checked={!!values.international_fee}
              onChange={handleToggleChange('international_fee')}
            />
            <ToggleSlider />
          </Toggle>
        </ToggleRow>
      </FormSection>
    </FormCard>
  );
};

PaymentsTab.propTypes = {
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

export default PaymentsTab;
